const isUndecodable = (segment: string): boolean => {
  try {
    decodeURIComponent(segment);
    return false;
  } catch {
    return true;
  }
};

/**
 * history v4 runs `decodeURI` on the pathname and throws a URIError on a
 * broken percent-escape, which blanks the whole app before any route renders.
 * Escapes the `%` of only the segments that fail to decode, so they decode
 * back to the original text. Returns null when the path is fine.
 */
const repairUndecodablePath = (pathname: string): string | null => {
  const segments = pathname.split('/');
  if (!segments.some(isUndecodable)) {
    return null;
  }
  return segments
    .map(segment =>
      isUndecodable(segment) ? segment.replace(/%/g, '%25') : segment
    )
    .join('/');
};

/**
 * Rewrites the current URL in place when its path can't be decoded. The URL
 * is absolute so a path starting with `//` stays on this origin.
 */
export const repairLocation = (
  location: Pick<Location, 'origin' | 'pathname' | 'search' | 'hash'>,
  history: Pick<History, 'state' | 'replaceState'>
): void => {
  const repairedPath = repairUndecodablePath(location.pathname);
  if (repairedPath === null) {
    return;
  }
  history.replaceState(
    history.state,
    '',
    `${location.origin}${repairedPath}${location.search}${location.hash}`
  );
};

export default repairUndecodablePath;
